import React, { Fragment, ReactElement, useMemo, useState } from "react";

import { TimeSeries } from "../../../../common/src";
import type { OpenTimeSeries } from "@captor/openseries-ts";
import { TimeSeriesLines } from "../../../../components/src/charts/TimeSeriesLines";
import { NamedSeries, seriesFromTimeSeries } from "../../../../components/src/charts/chartTheme";

type TimeSeriePlotPropsTypes = {
    tss: (TimeSeries | OpenTimeSeries)[];
    instrumentIds: string[];
    useRangeSelector?: boolean;
    customMargin?: Record<string, number>;
    colors?: string[];
    style?: React.CSSProperties;
    /** Plot y as (value - 1) with percent ticks, so a rebased price series starts at 0%. */
    asPercentFromOne?: boolean;
    /** Fired once after the chart mounts. */
    onInitialized?: () => void;
};

function seriesName(ts: TimeSeries | OpenTimeSeries): string {
    if ("label" in ts && ts.label) return ts.label;
    if ("name" in ts && ts.name) return ts.name;
    return "";
}

export const TimeSeriePlot = ({
    tss,
    useRangeSelector = true,
    colors,
    style,
    asPercentFromOne = false,
    onInitialized
}: TimeSeriePlotPropsTypes): ReactElement => {
    const [range, setRange] = useState<"all" | "ytd">("all");
    const series = useMemo(() => {
        const named: NamedSeries[] = tss.map((ts, index) => {
            const item = seriesFromTimeSeries(ts, colors?.[index]);
            item.name = seriesName(ts);
            if (asPercentFromOne) {
                item.points = item.points.map((point) => ({ date: point.date, value: point.value - 1 }));
            }
            return item;
        });
        if (range !== "ytd") return named;
        let latest = 0;
        for (const item of named) {
            for (const point of item.points) {
                if (point.date.getTime() > latest) latest = point.date.getTime();
            }
        }
        if (!latest) return named;
        const start = new Date(new Date(latest).getFullYear(), 0, 1).getTime();
        return named.map((item) => ({
            ...item,
            points: item.points.filter((point) => point.date.getTime() >= start)
        }));
    }, [asPercentFromOne, colors, range, tss]);

    const plotStyle = { width: "100%", height: "70vh", ...style };
    const first = series[0];
    const y0 = first?.points[0]?.value;
    const yLast = first?.points.length ? first.points[first.points.length - 1].value : undefined;
    const formatEndpoint = (value: number | undefined): string => {
        if (typeof value !== "number") return "";
        if (asPercentFromOne) return (value * 100).toFixed(1) + "%";
        return String(value);
    };
    const description = first ? `${first.name} from ${formatEndpoint(y0)} to ${formatEndpoint(yLast)}` : undefined;
    return (
        <Fragment>
            {useRangeSelector ? (
                <div className="mb-2 flex gap-2">
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setRange("ytd")}>
                        YTD
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setRange("all")}>
                        ALL
                    </button>
                </div>
            ) : null}
            <TimeSeriesLines
                series={series}
                yFormat={asPercentFromOne ? (value) => (value * 100).toFixed(1) + "%" : undefined}
                includeZero={asPercentFromOne}
                showLegend={series.length > 1}
                ariaLabel={series.map((item) => item.name).filter(Boolean).join(", ") || "Time series"}
                ariaDescription={description}
                height={420}
                style={plotStyle}
                onReady={onInitialized}
            />
        </Fragment>
    );
};
